import PageHeading from "../../../components/desktop/PageHeading";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { FaRegEdit, FaMapMarkerAlt } from "react-icons/fa";
import "./Locationmaster.css";
import back from "../../../assets/back.png";
import { useAuthStore } from "../../../store/authStore";
import { useLoaderStore } from "../../../store/loaderStore";
import { api } from "../../../utils/api";
import {
  loadGoogleMaps,
  getPlaceNameAt,
  getPlaceById,
  createPlacesSession,
  getPlaceSuggestions,
  getSuggestionDetails,
} from "../../../utils/googleMaps";

const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 }; // India

const EMPTY_FORM = {
  company_id: "",
  location_name: "",
  latitude: "",
  longitude: "",
  status: true,
};

function LocationPicker({ form, setForm }) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const posRef = useRef(null);

  const [mapReady, setMapReady] = useState(false);
  const [loadError, setLoadError] = useState("");

  const lat = Number(form.latitude);
  const lng = Number(form.longitude);
  const hasPosition =
    form.latitude !== "" &&
    form.longitude !== "" &&
    Number.isFinite(lat) &&
    Number.isFinite(lng);

  posRef.current = hasPosition ? { lat, lng } : null;

  // Set a point picked on the map and fill the location name from Google Places
  const selectPoint = useCallback(
    async (latLng, placeId) => {
      const pickedLat = latLng.lat().toFixed(6);
      const pickedLng = latLng.lng().toFixed(6);

      setForm((prev) => ({ ...prev, latitude: pickedLat, longitude: pickedLng }));

      try {
        const name = placeId
          ? (await getPlaceById(placeId))?.name
          : await getPlaceNameAt(pickedLat, pickedLng);
        if (name) {
          setForm((prev) => ({ ...prev, location_name: name }));
        }
        toast.success("Location selected successfully");
      } catch (error) {
        console.error("Place lookup error:", error);
      }
    },
    [setForm],
  );

  // Create map and marker once
  useEffect(() => {
    let cancelled = false;
    const listeners = [];

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapDivRef.current) return;

        const start = posRef.current;
        const map = new maps.Map(mapDivRef.current, {
          center: start || DEFAULT_CENTER,
          zoom: start ? 17 : 5,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });

        const marker = new maps.Marker({
          map,
          draggable: true,
          visible: false,
          title: "Drag to adjust location",
        });

        listeners.push(
          map.addListener("click", (e) => {
            // Clicking a business/landmark icon gives its placeId
            if (e.placeId) e.stop();
            selectPoint(e.latLng, e.placeId);
          }),
          marker.addListener("dragend", (e) => selectPoint(e.latLng)),
        );

        mapRef.current = map;
        markerRef.current = marker;
        setMapReady(true);
      })
      .catch((error) => {
        console.error(error);
        if (!cancelled) setLoadError(error.message);
      });

    return () => {
      cancelled = true;
      listeners.forEach((l) => l.remove());
      markerRef.current?.setMap(null);
    };
  }, [selectPoint]);

  // Keep marker in sync with the form (map click, GPS, typed lat/lng, place search)
  useEffect(() => {
    const map = mapRef.current;
    const marker = markerRef.current;
    if (!mapReady || !map || !marker) return;

    if (!hasPosition) {
      marker.setVisible(false);
      return;
    }

    const pos = { lat, lng };
    marker.setPosition(pos);
    marker.setVisible(true);

    if (map.getZoom() < 14) {
      map.setCenter(pos);
      map.setZoom(17);
    } else if (!map.getBounds()?.contains(pos)) {
      map.panTo(pos);
    }
  }, [mapReady, hasPosition, lat, lng]);

  return (
    <div className="location-picker-wrapper">
      <div className="location-map-holder">
        <div ref={mapDivRef} className="location-map" />
        {!mapReady && (
          <div className="map-status-overlay">
            {loadError || "Loading map..."}
          </div>
        )}
      </div>

      <div className="map-instructions">
        Click the map or drag the marker to set the location.
      </div>

      {/* {hasPosition && (
        <div className="coordinates-display">
          <span>📍 Lat: {form.latitude}</span>
          <span>Lng: {form.longitude}</span>
        </div>
      )} */}
    </div>
  );
}

const MIN_SEARCH_LENGTH = 2;
const SEARCH_DELAY_MS = 300;

// Location name input with Google Places autocomplete suggestions
function LocationNameSearch({ value, onChange, onSelect, children }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const sessionRef = useRef(null);
  const timerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const closeList = () => {
    // Ignore responses still in flight
    requestIdRef.current += 1;
    setOpen(false);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const fetchSuggestions = async (text) => {
    const requestId = ++requestIdRef.current;

    try {
      if (!sessionRef.current) {
        sessionRef.current = await createPlacesSession();
      }
      const results = await getPlaceSuggestions(text, sessionRef.current);
      if (requestId !== requestIdRef.current) return;

      setSuggestions(results);
      setActiveIndex(-1);
      setOpen(results.length > 0);
    } catch (error) {
      console.error("Place autocomplete error:", error);
    }
  };

  const handleInput = (e) => {
    const text = e.target.value;
    onChange(text);

    clearTimeout(timerRef.current);
    if (text.trim().length < MIN_SEARCH_LENGTH) {
      closeList();
      return;
    }
    timerRef.current = setTimeout(() => fetchSuggestions(text.trim()), SEARCH_DELAY_MS);
  };

  const handleSelect = async (suggestion) => {
    clearTimeout(timerRef.current);
    closeList();
    onChange(suggestion.text);

    try {
      const result = await getSuggestionDetails(suggestion);
      if (result) onSelect(result);
    } catch (error) {
      console.error("Place details error:", error);
      toast.error("Could not get this place's location");
    } finally {
      // Picking a place ends the billing session
      sessionRef.current = null;
    }
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter" && activeIndex >= 0) {
      // Enter should pick the suggestion, not submit the location form
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === "Escape") {
      closeList();
    }
  };

  return (
    <div className="location-name-autocomplete">
      <div className="location-name-input-wrapper">
        <input
          type="text"
          name="location_name"
          value={value}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          onBlur={closeList}
          placeholder="Search or enter location name"
          autoComplete="off"
          role="combobox"
          aria-controls="place-suggestions"
          aria-expanded={open}
          aria-autocomplete="list"
          required
        />
        {children}
      </div>

      {open && (
        <ul id="place-suggestions" className="place-suggestions" role="listbox">
          {suggestions.map((s, index) => (
            <li
              key={s.placeId}
              role="option"
              aria-selected={index === activeIndex}
              className={index === activeIndex ? "active" : ""}
              // mousedown fires before the input's blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(s);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <FaMapMarkerAlt className="place-suggestion-icon" />
              <div className="place-suggestion-text">
                <span className="place-main-text">{s.mainText}</span>
                {s.secondaryText && (
                  <span className="place-secondary-text">{s.secondaryText}</span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function Locationmaster() {
  const { user, isAuthenticated } = useAuthStore();
  const { showLoader, hideLoader } = useLoaderStore();

  const companyId = user?.company_id;

  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState(EMPTY_FORM);

  

  const fetchLocations = useCallback(async () => {
    try {
      showLoader("master");
      // const response = await fetch(
      //   `${API_BASE_URL}/location/list/${companyId}`,
      //   {
      //     method: "GET",
      //     headers: getHeaders(),
      //   },
      // );

      // const result = await response.json();

      const response = await api.get(`/location/list/${companyId}`);

      const result = await response.json();

      console.log("Location API Response:", result);

      if (!response.ok) {
        throw new Error(result.message || "Failed to load locations");
      }

      setLocations(result);
      toast.success("Locations fetched successfully");
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to load locations");
    } finally {
      hideLoader();
    }
  }, [companyId, isAuthenticated, showLoader, hideLoader]);

  useEffect(() => {
    if (companyId && isAuthenticated) {
      fetchLocations();
    }
  }, [companyId, isAuthenticated, fetchLocations]);

  const toggleStatus = async (item) => {
    try {
      showLoader("master");
      // const response = await fetch(
      //   `${API_BASE_URL}/location/update/${item.id}`,
      //   {
      //     method: "PUT",
      //     headers: getHeaders(),
      //     body: JSON.stringify({
      //       company_id: item.company_id,
      //       location_name: item.location_name,
      //       latitude: item.latitude,
      //       longitude: item.longitude,
      //       isactive: item.isactive ? 0 : 1,
      //     }),
      //   },
      // );

      const response = await api.put(`/location/update/${item.id}`, {
        company_id: item.company_id,
        location_name: item.location_name,
        latitude: item.latitude,
        longitude: item.longitude,
        isactive: item.isactive ? 0 : 1,
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message);
      }

      await fetchLocations();

      toast.success(
        `Location ${
          item.isactive === 1 ? "deactivated" : "activated"
        } successfully`,
      );
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Failed to update status");
    } finally {
      hideLoader();
    }
  };

  const [editId, setEditId] = useState(null);

  const handleEdit = async (item) => {
    setEditId(item.id);

    setForm({
      company_id: item.company_id,
      location_name: item.location_name,
      latitude: item.latitude,
      longitude: item.longitude,
      status: item.isactive === 1,
    });

    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const getGetCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    showLoader("master");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude.toFixed(6);
        const lng = position.coords.longitude.toFixed(6);
        const accuracy = position.coords.accuracy.toFixed(2);

        try {
          const address = await getPlaceNameAt(lat, lng);
          const locationName = address || `Location (${lat}, ${lng})`;

          setForm((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            location_name: locationName,
          }));

          toast.success(
            `📍 Current location obtained (Accuracy: ±${accuracy}m)`,
          );
        } catch (error) {
          console.error("Reverse geocode error:", error);

          setForm((prev) => ({
            ...prev,
            latitude: lat,
            longitude: lng,
            location_name: `Location (${lat}, ${lng})`,
          }));

          toast.success(`📍 Location obtained (Accuracy: ±${accuracy}m)`);
        } finally {
          hideLoader();
        }
      },
      (error) => {
        hideLoader();
        const errorMessages = {
          PERMISSION_DENIED: "Permission to access location was denied",
          POSITION_UNAVAILABLE: "Location information is unavailable",
          TIMEOUT: "The request to get user location timed out",
        };

        toast.error(
          errorMessages[error.code] || "Failed to get current location",
        );
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    );
  }, [showLoader, hideLoader, setForm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location_name.trim()) {
      toast.warning("Please enter location name");
      return;
    }

    if (!form.latitude || !form.longitude) {
      toast.warning("Please select a location from the map");
      return;
    }

    try {
      showLoader("master");
      const payload = {
        company_id: companyId,
        location_name: form.location_name,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        isactive: form.status ? 1 : 0,
      };

      let response;

      // if (editId) {
      //   response = await fetch(`${API_BASE_URL}/location/update/${editId}`, {
      //     method: "PUT",
      //     headers: getHeaders(),
      //     body: JSON.stringify(payload),
      //   });
      // } else {
      //   response = await fetch(`${API_BASE_URL}/location/add`, {
      //     method: "POST",
      //     headers: getHeaders(),
      //     body: JSON.stringify(payload),
      //   });
      // }

      if (editId) {
        response = await api.put(`/location/update/${editId}`, payload);
      } else {
        response = await api.post(`/location/add`, payload);
      }

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Operation failed");
      }

      await fetchLocations();

      toast.success(
        editId
          ? "Location updated successfully"
          : "Location added successfully",
      );

      setEditId(null);

      setForm(EMPTY_FORM);

      setShowForm(false);
    } catch (error) {
      console.error(error);
      toast.error(error.message || "Operation failed");
    } finally {
      hideLoader();
    }
  };

  return (
    <div className="location-master-container">
      <PageHeading>Location Master</PageHeading>
      {!showForm ? (
        <>
          <div className="location-master-top">
            <button
              className="add-location-btn"
              onClick={() => {
                setEditId(null);

                setForm(EMPTY_FORM);

                setShowForm(true);
              }}
            >
              + Add Location
            </button>
          </div>

          <div className="location-table-wrapper">
            <table className="location-table">
              <thead>
                <tr>
                  <th className="serial-col">Sr.No</th>
                  <th>Location Name</th>

                  <th className="status-col">Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {locations.map((item, index) => (
                  <tr key={item.id}>
                    <td className="serial-col">{index + 1}</td>

                    <td>{item.location_name}</td>

                    <td className="status-col">
                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={item.isactive === 1}
                          onChange={() => toggleStatus(item)}
                        />
                        <span className="slider"></span>
                      </label>
                    </td>

                    <td className="action-cell">
                      <FaRegEdit
                        className="edit-icon"
                        onClick={() => handleEdit(item)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="location-form-container">
          <div className="location-form-header">
            <img
              src={back}
              alt="Back"
              className="back-btn-img"
              onClick={() => setShowForm(false)}
            />

            <h3>{editId ? "Edit Location" : "Add Location"}</h3>
          </div>

          <form
            id="locationForm"
            onSubmit={handleSubmit}
            className="location-add-form"
          >
            <div className="form-group">
              <label>
                Location Name <span>*</span>
              </label>
              <LocationNameSearch
                value={form.location_name}
                onChange={(name) =>
                  setForm((prev) => ({ ...prev, location_name: name }))
                }
                onSelect={(place) =>
                  setForm((prev) => ({
                    ...prev,
                    location_name: place.name,
                    latitude: place.lat.toFixed(6),
                    longitude: place.lng.toFixed(6),
                  }))
                }
              >
                <button
                  type="button"
                  className="gps-button-inside"
                  onClick={getGetCurrentLocation}
                  title="Get current location"
                >
                  <FaMapMarkerAlt />
                </button>
              </LocationNameSearch>
            </div>

            <div className="form-group">
              <label>
                Latitude <span>*</span>
              </label>

              <input
                type="text"
                name="latitude"
                value={form.latitude}
                onChange={handleChange}
                placeholder="Enter Latitude"
              />
            </div>

            <div className="form-group">
              <label>
                Longitude <span>*</span>
              </label>

              <input
                type="text"
                name="longitude"
                value={form.longitude}
                onChange={handleChange}
                placeholder="Enter Longitude"
              />
            </div>

            {editId && (
              <div className="form-group">
                <label>Status</label>

                <select
                  name="status"
                  value={form.status ? "Active" : "Inactive"}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      status: e.target.value === "Active",
                    })
                  }
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            )}

            <div className="form-group map-group">
              <label>
                Select Location <span>*</span>
              </label>

              <LocationPicker form={form} setForm={setForm} />
            </div>
          </form>

          {/* Footer inside desktop content */}
          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => {
                setEditId(null);

                setForm(EMPTY_FORM);

                setShowForm(false);
              }}
            >
              Cancel
            </button>

            <button type="submit" form="locationForm" className="save-btn">
              {editId ? "Update" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
